import type {
  PlatformFeeCollectionMode,
  PlatformFeePolicySource,
  PlatformFeeSnapshot,
} from "./billing.js";

export const spaceTypeIds = [
  "eventHall",
  "multipurposeRoom",
  "classroom",
  "kitchen",
  "gym",
  "meetingRoom",
  "outdoorSpace",
  "parking",
  "equipment",
  "other",
] as const;
export type SpaceTypeId = (typeof spaceTypeIds)[number];

export const spaceStatuses = ["draft", "active", "unavailable", "archived"] as const;
export type OrganizationSpaceStatus = (typeof spaceStatuses)[number];

export const spaceVisibilities = ["private", "connected", "public"] as const;
export type OrganizationSpaceVisibility = (typeof spaceVisibilities)[number];

export const spaceAmenityIds = [
  "tables",
  "chairs",
  "kitchenAccess",
  "projector",
  "audioSystem",
  "stage",
  "parking",
  "wheelchairAccessible",
  "restrooms",
  "wifi",
] as const;
export type SpaceAmenityId = (typeof spaceAmenityIds)[number];

export const spaceAccessibilityFeatureIds = [
  "wheelchairAccessible",
  "accessibleEntrance",
  "accessibleRestroom",
  "elevator",
] as const;
export type SpaceAccessibilityFeatureId = (typeof spaceAccessibilityFeatureIds)[number];

export const spaceAvailabilityBlockTypes = ["unavailable", "adminHold", "maintenance"] as const;
export type SpaceAvailabilityBlockType = (typeof spaceAvailabilityBlockTypes)[number];

export const spaceAvailabilityStatuses = ["available", "closed", "blocked", "unavailable"] as const;
export type SpaceAvailabilityStatus = (typeof spaceAvailabilityStatuses)[number];

export const spaceReservationStatuses = ["pending", "approved", "rejected", "cancelled"] as const;
export type SpaceReservationStatus = (typeof spaceReservationStatuses)[number];

export const spacePricingTypes = ["flat", "hourly"] as const;
export type SpacePricingType = (typeof spacePricingTypes)[number];

export const spaceRateUnits = ["reservation", "hour"] as const;
export type SpaceRateUnit = (typeof spaceRateUnits)[number];

export const spaceAgreementStatuses = ["active", "archived"] as const;
export type SpaceAgreementStatus = (typeof spaceAgreementStatuses)[number];

export const spaceReservationPaymentStatuses = [
  "notRequired",
  "unpaid",
  "processing",
  "paid",
  "paymentFailed",
  "partiallyRefunded",
  "refunded",
] as const;
export type SpaceReservationPaymentStatus = (typeof spaceReservationPaymentStatuses)[number];

export const spaceReservationSources = ["experience_forty"] as const;
export type CanonicalSpaceReservationSource = (typeof spaceReservationSources)[number];

export const spaceDayIds = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;
export type SpaceDayId = (typeof spaceDayIds)[number];

export type SpaceLocalizedText = {
  en: string;
  ar: string | null;
};

export type SpaceMediaReference = {
  storagePath: string;
  altText: SpaceLocalizedText;
  visibility: "inherit" | "connected" | "public";
  sortOrder: number;
};

export type SpaceAccessibility = {
  features: SpaceAccessibilityFeatureId[];
  notes: SpaceLocalizedText | null;
};

export type SpaceOperatingHours = Record<SpaceDayId, string>;

export type SpaceFixedFee = {
  amountMinor: number;
  feeId: string;
  label: SpaceLocalizedText;
};

export type SpacePricingConfiguration = {
  activeAgreementId: string | null;
  agreementRequired: boolean;
  baseRateMinor: number;
  currency: "usd";
  depositMinor: number;
  effectiveFrom: unknown | null;
  fixedFees: SpaceFixedFee[];
  minimumChargeMinor: number;
  pricingEnabled: boolean;
  pricingType: SpacePricingType;
  pricingVersion: number;
  rateUnit: SpaceRateUnit;
  source: "experience_forty_one";
  updatedAt: unknown;
  updatedBy: string;
};

export type SpaceReservationPricingSnapshot = {
  additionalFeeAmountMinor: number;
  agreementBodyHash: string | null;
  agreementBodySnapshot: SpaceLocalizedText | null;
  agreementId: string | null;
  agreementRequired: boolean;
  agreementTitleSnapshot: SpaceLocalizedText | null;
  agreementVersion: number | null;
  baseAmountMinor: number;
  billableMinutes: number;
  calculatedAt: unknown;
  calculatedBy: string;
  currency: "usd";
  depositAmountMinor: number;
  durationMinutes: number;
  fixedFees: SpaceFixedFee[];
  minimumChargeMinor: number;
  organizationId: string;
  organizationNetMinor: number;
  payerTotalMinor?: number;
  platformFeeCollectionMode?: PlatformFeeCollectionMode;
  platformFeePolicyId?: string | null;
  platformFeePolicySource?: PlatformFeePolicySource;
  platformFeePolicyVersion?: number | null;
  platformFeeMinor: number;
  platformFeeSnapshot?: PlatformFeeSnapshot;
  pricingSnapshotId: string;
  pricingType: SpacePricingType;
  pricingVersion: number;
  rateMinor: number;
  rateUnit: SpaceRateUnit;
  reservationId: string;
  source: "experience_forty_one";
  spaceId: string;
  totalAmountMinor: number;
};

export type SpaceAgreementRecord = {
  agreementId: string;
  body: SpaceLocalizedText;
  bodyHash: string;
  createdAt: unknown;
  createdBy: string;
  effectiveAt: unknown;
  organizationId: string;
  source: "experience_forty_one";
  spaceId: string;
  status: SpaceAgreementStatus;
  title: SpaceLocalizedText;
  updatedAt: unknown;
  updatedBy: string;
  version: number;
};

export type SpaceAgreementAcceptanceSnapshot = {
  acceptedAt: unknown;
  acceptedByPersonId: string;
  acceptanceId: string;
  agreementBodyHash: string;
  agreementId: string;
  agreementTitleSnapshot: SpaceLocalizedText;
  agreementVersion: number;
  authUid: string;
  organizationId: string;
  reservationId: string;
  source: "experience_forty_one";
  spaceId: string;
};

export type SpaceReservationTransaction = {
  amountMinor: number;
  baseAmountMinor: number;
  cancelledAt: unknown | null;
  confirmedAt: unknown | null;
  createdAt: unknown;
  currency: "usd";
  depositAmountMinor: number;
  failedAt: unknown | null;
  fixedFeeAmountMinor: number;
  grossAmountMinor: number;
  idempotencyKeyHash: string;
  organizationId: string;
  organizationNetMinor: number;
  payerTotalMinor?: number;
  personId: string;
  platformFeeCollectionMode?: PlatformFeeCollectionMode;
  platformFeePolicyId?: string | null;
  platformFeePolicySource?: PlatformFeePolicySource;
  platformFeePolicyVersion?: number | null;
  platformFeeMinor: number;
  platformFeeSnapshot?: PlatformFeeSnapshot;
  pricingSnapshotId: string;
  provider: "stripe";
  providerChargeId: string | null;
  providerConnectedAccountId: string;
  providerPaymentIntentId: string | null;
  providerStatus: string;
  receiptId: string | null;
  refundedAmountMinor: number;
  refundPendingAmountMinor: number;
  refundStatus: "none" | "processing" | "partiallyRefunded" | "refunded" | "failed";
  remainingRefundableAmountMinor: number;
  requesterDisplayNameSnapshot: string | null;
  reservationId: string;
  source: "experience_forty_one";
  spaceId: string;
  status: "pending" | "requiresAction" | "processing" | "confirmed" | "failed" | "cancelled";
  totalPaymentAmountMinor: number;
  transactionId: string;
  transactionType: "spaceReservation";
  updatedAt: unknown;
};

export type SpaceReservationLedgerEntry = {
  amountMinor: number;
  createdAt: unknown;
  createdBy: "system:stripeConnectWebhook";
  currency: "usd";
  direction: "credit";
  ledgerEntryId: string;
  organizationId: string;
  provider: "stripe";
  providerPaymentIntentId: string;
  pricingSnapshotId: string;
  reservationId: string;
  source: "experience_forty_one";
  spaceId: string;
  transactionId: string;
  type: "space_reservation_organization_net";
};

export type SpaceReservationReceipt = {
  amountMinor: number;
  baseAmountMinor: number;
  confirmedAt: unknown;
  createdAt: unknown;
  currency: "usd";
  depositAmountMinor: number;
  fixedFeeAmountMinor: number;
  organizationId: string;
  personId: string;
  pricingSnapshotId: string;
  provider: "stripe";
  providerPaymentIntentId: string;
  receiptId: string;
  receiptNumber: string;
  receiptType: "spaceReservation";
  reservationId: string;
  requesterDisplayNameSnapshot: string | null;
  source: "experience_forty_one";
  spaceId: string;
  totalPaymentAmountMinor: number;
  transactionId: string;
};

export type SpaceReservationPaymentIdempotencyRecord = {
  currency: "usd";
  idempotencyDocumentId: string;
  idempotencyKeyHash: string;
  organizationId: string;
  personId: string;
  pricingSnapshotId: string;
  providerClientSecret: string | null;
  providerPaymentIntentId: string | null;
  reservationId: string;
  source: "experience_forty_one";
  spaceId: string;
  status: "creating" | "ready";
  totalPaymentAmountMinor: number;
  transactionId: string;
};

export type SpaceRecord = {
  spaceId: string;
  organizationId: string;
  campusId: string | null;
  type: SpaceTypeId;
  status: OrganizationSpaceStatus;
  visibility: OrganizationSpaceVisibility;
  name: string;
  nameAr: string | null;
  description: string;
  descriptionAr: string | null;
  locationName: string;
  capacity: number;
  amenities: SpaceAmenityId[];
  accessibility: SpaceAccessibility;
  primaryImage: SpaceMediaReference | null;
  galleryImages: SpaceMediaReference[];
  timezone: string;
  operatingHours: SpaceOperatingHours;
  hourlyPriceCents: number | null;
  currency: "usd" | null;
  pricing?: SpacePricingConfiguration | null;
  source: "experience_thirty_nine";
  createdAt: unknown;
  createdBy: string;
  updatedAt: unknown;
  updatedBy: string;
};

export type SpaceAvailabilityBlockRecord = {
  blockId: string;
  organizationId: string;
  spaceId: string;
  type: SpaceAvailabilityBlockType;
  startsAt: Date;
  endsAt: Date;
  label: SpaceLocalizedText;
  internalReason: string | null;
  publicReason: boolean;
  source: "experience_thirty_nine";
  createdAt: unknown;
  createdBy: string;
  updatedAt: unknown;
  updatedBy: string;
};

export type SpaceReservationRequesterSnapshot = {
  displayName: string;
  email: string | null;
};

export type SpaceReservationRecord = {
  reservationId: string;
  organizationId: string;
  spaceId: string;
  requesterPersonId: string;
  requesterSnapshot: SpaceReservationRequesterSnapshot;
  status: SpaceReservationStatus;
  startsAt: Date;
  endsAt: Date;
  timezone: string;
  purpose: string;
  guestCount: number;
  notes: string | null;
  requestedAt: unknown;
  reviewedAt: unknown | null;
  reviewedBy: string | null;
  approvedAt: unknown | null;
  rejectedAt: unknown | null;
  rejectionReason: string | null;
  cancelledAt: unknown | null;
  cancelledBy: string | null;
  cancellationReason: string | null;
  agreementAcceptance?: SpaceAgreementAcceptanceSnapshot | null;
  paidAt?: unknown | null;
  paymentDeadlineAt?: unknown | null;
  paymentStatus?: SpaceReservationPaymentStatus;
  paymentUpdatedAt?: unknown | null;
  pricingSnapshot?: SpaceReservationPricingSnapshot | null;
  providerPaymentIntentId?: string | null;
  receiptId?: string | null;
  transactionId?: string | null;
  source: CanonicalSpaceReservationSource;
  createdAt: unknown;
  createdBy: string;
  updatedAt: unknown;
  updatedBy: string;
};

export type SpaceReservationConflictInterval = Pick<
  SpaceReservationRecord,
  "endsAt" | "reservationId" | "spaceId" | "startsAt" | "status"
>;

export type SpaceAvailabilityEvaluationInput = {
  at: Date;
  blocks?: SpaceAvailabilityBlockRecord[];
  reservationConflicts?: SpaceReservationConflictInterval[];
  space: Pick<SpaceRecord, "operatingHours" | "status" | "timezone">;
};

export type SpaceAvailabilityEvaluation = {
  status: SpaceAvailabilityStatus;
  matchingBlockId: string | null;
  matchingReservationId: string | null;
  reason:
    | "space_lifecycle"
    | "outside_operating_hours"
    | "availability_block"
    | "approved_reservation"
    | "open";
};

export type SpaceReservationIntervalEvaluationInput = {
  blocks?: SpaceAvailabilityBlockRecord[];
  reservationConflicts?: SpaceReservationConflictInterval[];
  endsAt: Date;
  space: Pick<SpaceRecord, "operatingHours" | "spaceId" | "status" | "timezone">;
  startsAt: Date;
};

export type SpaceReservationIntervalEvaluation = {
  available: boolean;
  matchingBlockId: string | null;
  matchingReservationId: string | null;
  reason:
    | "invalid_interval"
    | "space_lifecycle"
    | "outside_operating_hours"
    | "availability_block"
    | "approved_reservation"
    | "open";
};

const safeSpaceIdPattern = /^[a-z0-9][a-z0-9_-]{0,79}$/;
const safeAgreementIdPattern = /^[a-z0-9][a-z0-9_-]{0,99}$/;
const safeStoragePathPattern =
  /^organizations\/[a-z0-9_-]+\/spaces\/[a-z0-9][a-z0-9_-]{0,79}\/[A-Za-z0-9][A-Za-z0-9._/-]{0,240}$/;
const timeIntervalPattern = /^(([01]\d|2[0-3]):[0-5]\d)-(([01]\d|2[0-3]):[0-5]\d)$/;
const maximumSpacePaymentMinor = 100_000_000;

export function normalizeSpaceId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export function validateSpaceRecord(space: SpaceRecord): string[] {
  const errors: string[] = [];

  if (!safeSpaceIdPattern.test(space.spaceId)) {
    errors.push("Space ID must be a safe immutable identifier.");
  }
  if (!space.organizationId) {
    errors.push("Space organization is required.");
  }
  if (space.campusId !== null && !safeSpaceIdPattern.test(space.campusId)) {
    errors.push("Space campus ID must be safe when present.");
  }
  if (!spaceTypeIds.includes(space.type)) {
    errors.push("Space type must be canonical.");
  }
  if (!spaceStatuses.includes(space.status)) {
    errors.push("Space status must be canonical.");
  }
  if (!spaceVisibilities.includes(space.visibility)) {
    errors.push("Space visibility must be canonical.");
  }
  validateRequiredText("Space English name", space.name, 120, errors);
  validateOptionalText("Space Arabic name", space.nameAr, 120, errors);
  validateRequiredText("Space English description", space.description, 800, errors);
  validateOptionalText("Space Arabic description", space.descriptionAr, 800, errors);
  validateRequiredText("Space location label", space.locationName, 160, errors);
  if (!Number.isSafeInteger(space.capacity) || space.capacity < 0 || space.capacity > 5000) {
    errors.push("Space capacity must be a non-negative integer no greater than 5,000.");
  }
  if (!Array.isArray(space.amenities) || space.amenities.length > 20) {
    errors.push("Space amenities must be a bounded list.");
  } else if (space.amenities.some((amenity) => !spaceAmenityIds.includes(amenity))) {
    errors.push("Space amenities must use canonical identifiers.");
  }
  validateAccessibility(space.accessibility, errors);
  validateMediaReference("Space primary image", space.primaryImage, space, errors);
  if (!Array.isArray(space.galleryImages) || space.galleryImages.length > 8) {
    errors.push("Space gallery images must be a bounded list.");
  } else {
    for (const media of space.galleryImages) {
      validateMediaReference("Space gallery image", media, space, errors);
    }
  }
  validateRequiredText("Space timezone", space.timezone, 80, errors);
  validateOperatingHours(space.operatingHours, errors);
  if (
    space.hourlyPriceCents !== null &&
    (!Number.isSafeInteger(space.hourlyPriceCents) ||
      space.hourlyPriceCents < 0 ||
      space.hourlyPriceCents > 100_000)
  ) {
    errors.push("Legacy hourly price must be a non-negative USD minor-unit amount.");
  }
  if (space.hourlyPriceCents === null && space.currency !== null) {
    errors.push("Space currency must be null when no legacy hourly price is present.");
  }
  if (space.hourlyPriceCents !== null && space.currency !== "usd") {
    errors.push("Legacy hourly price currency must be usd.");
  }
  if (space.pricing !== undefined && space.pricing !== null) {
    validateSpacePricingConfiguration(space.pricing, errors);
  }
  if (space.source !== "experience_thirty_nine") {
    errors.push("Space source must be experience_thirty_nine.");
  }

  return errors;
}

export function validateSpaceAvailabilityBlock(block: SpaceAvailabilityBlockRecord): string[] {
  const errors: string[] = [];

  if (!safeSpaceIdPattern.test(block.blockId)) {
    errors.push("Availability block ID must be a safe immutable identifier.");
  }
  if (!block.organizationId) {
    errors.push("Availability block organization is required.");
  }
  if (!safeSpaceIdPattern.test(block.spaceId)) {
    errors.push("Availability block space ID must be safe.");
  }
  if (!spaceAvailabilityBlockTypes.includes(block.type)) {
    errors.push("Availability block type must be canonical.");
  }
  if (!(block.startsAt instanceof Date) || Number.isNaN(block.startsAt.getTime())) {
    errors.push("Availability block start must be a valid Date.");
  }
  if (!(block.endsAt instanceof Date) || Number.isNaN(block.endsAt.getTime())) {
    errors.push("Availability block end must be a valid Date.");
  }
  if (
    block.startsAt instanceof Date &&
    block.endsAt instanceof Date &&
    !Number.isNaN(block.startsAt.getTime()) &&
    !Number.isNaN(block.endsAt.getTime()) &&
    block.endsAt <= block.startsAt
  ) {
    errors.push("Availability block end must be after start.");
  }
  validateLocalizedText("Availability block label", block.label, 120, errors);
  validateOptionalText("Availability block internal reason", block.internalReason, 500, errors);
  if (typeof block.publicReason !== "boolean") {
    errors.push("Availability block public reason flag must be boolean.");
  }
  if (block.source !== "experience_thirty_nine") {
    errors.push("Availability block source must be experience_thirty_nine.");
  }

  return errors;
}

export function validateSpaceReservationRecord(
  reservation: SpaceReservationRecord,
  space?: Pick<SpaceRecord, "capacity" | "organizationId" | "spaceId">,
): string[] {
  const errors: string[] = [];

  if (!/^spr_[a-f0-9]{24,64}$/.test(reservation.reservationId)) {
    errors.push("Space reservation ID must be an opaque immutable identifier.");
  }
  if (!reservation.organizationId) {
    errors.push("Space reservation organization is required.");
  }
  if (!safeSpaceIdPattern.test(reservation.spaceId)) {
    errors.push("Space reservation space ID must be safe.");
  }
  if (space && reservation.organizationId !== space.organizationId) {
    errors.push("Space reservation organization must match the Space.");
  }
  if (space && reservation.spaceId !== space.spaceId) {
    errors.push("Space reservation space ID must match the Space.");
  }
  if (!safePersonId(reservation.requesterPersonId)) {
    errors.push("Space reservation requester must be a safe Person ID.");
  }
  validateRequesterSnapshot(reservation.requesterSnapshot, errors);
  if (!spaceReservationStatuses.includes(reservation.status)) {
    errors.push("Space reservation status must be canonical.");
  }
  if (!(reservation.startsAt instanceof Date) || Number.isNaN(reservation.startsAt.getTime())) {
    errors.push("Space reservation start must be a valid Date.");
  }
  if (!(reservation.endsAt instanceof Date) || Number.isNaN(reservation.endsAt.getTime())) {
    errors.push("Space reservation end must be a valid Date.");
  }
  if (
    reservation.startsAt instanceof Date &&
    reservation.endsAt instanceof Date &&
    !Number.isNaN(reservation.startsAt.getTime()) &&
    !Number.isNaN(reservation.endsAt.getTime()) &&
    reservation.endsAt <= reservation.startsAt
  ) {
    errors.push("Space reservation end must be after start.");
  }
  if (!Number.isSafeInteger(reservation.guestCount) || reservation.guestCount < 1) {
    errors.push("Space reservation guest count must be a positive integer.");
  }
  if (
    space &&
    space.capacity > 0 &&
    Number.isSafeInteger(reservation.guestCount) &&
    reservation.guestCount > space.capacity
  ) {
    errors.push("Space reservation guest count cannot exceed Space capacity.");
  }
  validateRequiredText("Space reservation purpose", reservation.purpose, 240, errors);
  validateOptionalText("Space reservation notes", reservation.notes, 800, errors);
  validateOptionalText(
    "Space reservation rejection reason",
    reservation.rejectionReason,
    500,
    errors,
  );
  validateOptionalText(
    "Space reservation cancellation reason",
    reservation.cancellationReason,
    500,
    errors,
  );
  validateRequiredText("Space reservation timezone", reservation.timezone, 80, errors);
  if (
    reservation.paymentStatus !== undefined &&
    !spaceReservationPaymentStatuses.includes(reservation.paymentStatus)
  ) {
    errors.push("Space reservation payment status must be canonical.");
  }
  if (reservation.pricingSnapshot !== undefined && reservation.pricingSnapshot !== null) {
    validateSpaceReservationPricingSnapshot(reservation.pricingSnapshot, reservation, errors);
  }
  if (reservation.agreementAcceptance !== undefined && reservation.agreementAcceptance !== null) {
    validateSpaceAgreementAcceptanceSnapshot(reservation.agreementAcceptance, reservation, errors);
  }
  if (reservation.source !== "experience_forty") {
    errors.push("Space reservation source must be experience_forty.");
  }

  return errors;
}

export function evaluateSpaceAvailability(
  input: SpaceAvailabilityEvaluationInput,
): SpaceAvailabilityEvaluation {
  if (input.space.status !== "active") {
    return {
      matchingBlockId: null,
      matchingReservationId: null,
      reason: "space_lifecycle",
      status: "unavailable",
    };
  }

  const local = localDayAndMinute(input.at, input.space.timezone);
  const schedule = input.space.operatingHours[local.day];

  if (!isWithinOperatingHours(schedule, local.minuteOfDay)) {
    return {
      matchingBlockId: null,
      matchingReservationId: null,
      reason: "outside_operating_hours",
      status: "closed",
    };
  }

  const matchingBlock = (input.blocks ?? [])
    .filter(
      (block) => block.spaceId.length > 0 && block.startsAt <= input.at && input.at < block.endsAt,
    )
    .sort((left, right) => left.startsAt.getTime() - right.startsAt.getTime())[0];

  if (matchingBlock) {
    return {
      matchingBlockId: matchingBlock.blockId,
      matchingReservationId: null,
      reason: "availability_block",
      status: "blocked",
    };
  }

  const matchingReservation = (input.reservationConflicts ?? [])
    .filter(
      (reservation) =>
        reservation.status === "approved" &&
        reservation.spaceId.length > 0 &&
        reservation.startsAt <= input.at &&
        input.at < reservation.endsAt,
    )
    .sort((left, right) => left.startsAt.getTime() - right.startsAt.getTime())[0];

  if (matchingReservation) {
    return {
      matchingBlockId: null,
      matchingReservationId: matchingReservation.reservationId,
      reason: "approved_reservation",
      status: "blocked",
    };
  }

  return {
    matchingBlockId: null,
    matchingReservationId: null,
    reason: "open",
    status: "available",
  };
}

export function evaluateSpaceReservationInterval(
  input: SpaceReservationIntervalEvaluationInput,
): SpaceReservationIntervalEvaluation {
  if (
    !(input.startsAt instanceof Date) ||
    !(input.endsAt instanceof Date) ||
    Number.isNaN(input.startsAt.getTime()) ||
    Number.isNaN(input.endsAt.getTime()) ||
    input.endsAt <= input.startsAt
  ) {
    return {
      available: false,
      matchingBlockId: null,
      matchingReservationId: null,
      reason: "invalid_interval",
    };
  }

  if (input.space.status !== "active") {
    return {
      available: false,
      matchingBlockId: null,
      matchingReservationId: null,
      reason: "space_lifecycle",
    };
  }

  if (!isIntervalWithinOperatingHours(input.startsAt, input.endsAt, input.space)) {
    return {
      available: false,
      matchingBlockId: null,
      matchingReservationId: null,
      reason: "outside_operating_hours",
    };
  }

  const matchingBlock = (input.blocks ?? [])
    .filter(
      (block) =>
        block.spaceId === input.space.spaceId &&
        spaceIntervalsOverlap(block.startsAt, block.endsAt, input.startsAt, input.endsAt),
    )
    .sort((left, right) => left.startsAt.getTime() - right.startsAt.getTime())[0];

  if (matchingBlock) {
    return {
      available: false,
      matchingBlockId: matchingBlock.blockId,
      matchingReservationId: null,
      reason: "availability_block",
    };
  }

  const matchingReservation = (input.reservationConflicts ?? [])
    .filter(
      (reservation) =>
        reservation.status === "approved" &&
        reservation.spaceId === input.space.spaceId &&
        spaceIntervalsOverlap(
          reservation.startsAt,
          reservation.endsAt,
          input.startsAt,
          input.endsAt,
        ),
    )
    .sort((left, right) => left.startsAt.getTime() - right.startsAt.getTime())[0];

  if (matchingReservation) {
    return {
      available: false,
      matchingBlockId: null,
      matchingReservationId: matchingReservation.reservationId,
      reason: "approved_reservation",
    };
  }

  return {
    available: true,
    matchingBlockId: null,
    matchingReservationId: null,
    reason: "open",
  };
}

export function spaceIntervalsOverlap(
  existingStartsAt: Date,
  existingEndsAt: Date,
  requestedStartsAt: Date,
  requestedEndsAt: Date,
): boolean {
  return existingStartsAt < requestedEndsAt && existingEndsAt > requestedStartsAt;
}

export function defaultSpaceOperatingHours(): SpaceOperatingHours {
  return {
    friday: "09:00-22:00",
    monday: "09:00-22:00",
    saturday: "09:00-22:00",
    sunday: "09:00-22:00",
    thursday: "09:00-22:00",
    tuesday: "09:00-22:00",
    wednesday: "09:00-22:00",
  };
}

export function calculateSpaceReservationCharge(input: {
  endsAt: Date;
  pricing: SpacePricingConfiguration;
  startsAt: Date;
}): {
  additionalFeeAmountMinor: number;
  baseAmountMinor: number;
  billableMinutes: number;
  depositAmountMinor: number;
  durationMinutes: number;
  fixedFees: SpaceFixedFee[];
  totalAmountMinor: number;
} {
  if (!input.pricing.pricingEnabled) {
    return {
      additionalFeeAmountMinor: 0,
      baseAmountMinor: 0,
      billableMinutes: 0,
      depositAmountMinor: 0,
      durationMinutes: 0,
      fixedFees: [],
      totalAmountMinor: 0,
    };
  }
  if (input.endsAt <= input.startsAt) {
    throw new Error("space_reservation_interval_invalid");
  }

  const durationMinutes = Math.ceil((input.endsAt.getTime() - input.startsAt.getTime()) / 60_000);
  const billableMinutes =
    input.pricing.pricingType === "hourly" ? Math.max(durationMinutes, 1) : durationMinutes;
  const rawBaseAmountMinor =
    input.pricing.pricingType === "hourly"
      ? Math.ceil((input.pricing.baseRateMinor * billableMinutes) / 60)
      : input.pricing.baseRateMinor;
  const baseAmountMinor = Math.max(rawBaseAmountMinor, input.pricing.minimumChargeMinor);
  const fixedFees = input.pricing.fixedFees.map((fee) => ({ ...fee }));
  const additionalFeeAmountMinor = fixedFees.reduce((sum, fee) => sum + fee.amountMinor, 0);
  const totalAmountMinor = baseAmountMinor + input.pricing.depositMinor + additionalFeeAmountMinor;
  if (!isMinorAmount(totalAmountMinor) || totalAmountMinor > maximumSpacePaymentMinor) {
    throw new Error("space_reservation_amount_invalid");
  }

  return {
    additionalFeeAmountMinor,
    baseAmountMinor,
    billableMinutes,
    depositAmountMinor: input.pricing.depositMinor,
    durationMinutes,
    fixedFees,
    totalAmountMinor,
  };
}

export function spaceReservationPricingSnapshotIdFor(reservationId: string): string {
  return `spsnap_${reservationId.replace(/^spr_/u, "")}`;
}

export function spaceReservationAgreementAcceptanceIdFor(input: {
  agreementId: string;
  personId: string;
  reservationId: string;
  version: number;
}): string {
  return `spaccept_${stableTextHash(
    `${input.reservationId}:${input.agreementId}:${input.version}:${input.personId}`,
  ).slice(0, 32)}`;
}

export function spaceReservationTransactionIdFor(input: {
  idempotencyKey: string;
  organizationId: string;
  personId: string;
  reservationId: string;
  spaceId: string;
}): string {
  return `txn_${stableTextHash(
    `space:${input.organizationId}:${input.spaceId}:${input.reservationId}:${input.personId}:${input.idempotencyKey}`,
  ).slice(0, 32)}`;
}

export function spaceReservationPaymentIdempotencyDocumentIdFor(input: {
  idempotencyKey: string;
  personId: string;
  reservationId: string;
}): string {
  return `idem_${stableTextHash(
    `${input.personId}:${input.reservationId}:${input.idempotencyKey}`,
  ).slice(0, 48)}`;
}

export function spaceReservationLedgerEntryIdFor(transactionId: string): string {
  return `${transactionId}_space_reservation_organization_net`;
}

export function spaceReservationReceiptIdFor(transactionId: string): string {
  return `rcpt_${transactionId.replace(/^txn_/u, "")}`;
}

export function spaceReservationReceiptNumberFor(transactionId: string): string {
  return `SHD-SPACE-${transactionId.replace(/^txn_/u, "").slice(0, 10).toUpperCase()}`;
}

export function closedSpaceOperatingHours(): SpaceOperatingHours {
  return {
    friday: "closed",
    monday: "closed",
    saturday: "closed",
    sunday: "closed",
    thursday: "closed",
    tuesday: "closed",
    wednesday: "closed",
  };
}

export function validateSpaceAgreementRecord(agreement: SpaceAgreementRecord): string[] {
  const errors: string[] = [];

  if (!safeAgreementIdPattern.test(agreement.agreementId)) {
    errors.push("Space agreement ID must be safe.");
  }
  if (!agreement.organizationId) {
    errors.push("Space agreement organization is required.");
  }
  if (!safeSpaceIdPattern.test(agreement.spaceId)) {
    errors.push("Space agreement Space ID must be safe.");
  }
  if (!Number.isSafeInteger(agreement.version) || agreement.version < 1) {
    errors.push("Space agreement version must be a positive integer.");
  }
  if (!spaceAgreementStatuses.includes(agreement.status)) {
    errors.push("Space agreement status must be canonical.");
  }
  validateLocalizedText("Space agreement title", agreement.title, 180, errors);
  validateLocalizedText("Space agreement body", agreement.body, 6000, errors);
  if (typeof agreement.bodyHash !== "string" || agreement.bodyHash.length < 12) {
    errors.push("Space agreement body hash must be stored.");
  }
  if (agreement.source !== "experience_forty_one") {
    errors.push("Space agreement source must be experience_forty_one.");
  }

  return errors;
}

function validateSpacePricingConfiguration(
  pricing: SpacePricingConfiguration,
  errors: string[],
): void {
  if (!isRecord(pricing)) {
    errors.push("Space pricing must be structured.");
    return;
  }
  if (typeof pricing.pricingEnabled !== "boolean") {
    errors.push("Space pricing enabled flag must be boolean.");
  }
  if (pricing.currency !== "usd") {
    errors.push("Space pricing currency must be usd.");
  }
  if (!spacePricingTypes.includes(pricing.pricingType)) {
    errors.push("Space pricing type must be canonical.");
  }
  if (!spaceRateUnits.includes(pricing.rateUnit)) {
    errors.push("Space pricing rate unit must be canonical.");
  }
  if (pricing.pricingType === "flat" && pricing.rateUnit !== "reservation") {
    errors.push("Flat Space pricing must use reservation rate unit.");
  }
  if (pricing.pricingType === "hourly" && pricing.rateUnit !== "hour") {
    errors.push("Hourly Space pricing must use hour rate unit.");
  }
  if (!isMinorAmount(pricing.baseRateMinor)) {
    errors.push("Space base rate must use integer minor units.");
  }
  if (pricing.pricingEnabled && pricing.baseRateMinor <= 0) {
    errors.push("Enabled Space pricing requires a positive base rate.");
  }
  if (!isMinorAmount(pricing.minimumChargeMinor)) {
    errors.push("Space minimum charge must use integer minor units.");
  }
  if (!isMinorAmount(pricing.depositMinor)) {
    errors.push("Space deposit must use integer minor units.");
  }
  validateSpaceFixedFees(pricing.fixedFees, errors);
  if (!Number.isSafeInteger(pricing.pricingVersion) || pricing.pricingVersion < 1) {
    errors.push("Space pricing version must be a positive integer.");
  }
  if (typeof pricing.agreementRequired !== "boolean") {
    errors.push("Space pricing agreement required flag must be boolean.");
  }
  if (
    pricing.activeAgreementId !== null &&
    !safeAgreementIdPattern.test(pricing.activeAgreementId)
  ) {
    errors.push("Space active agreement ID must be safe when present.");
  }
  if (pricing.agreementRequired && pricing.activeAgreementId === null) {
    errors.push("Required Space agreement must reference an active agreement.");
  }
  if (pricing.source !== "experience_forty_one") {
    errors.push("Space pricing source must be experience_forty_one.");
  }
}

function validateSpaceReservationPricingSnapshot(
  snapshot: SpaceReservationPricingSnapshot,
  reservation: SpaceReservationRecord,
  errors: string[],
): void {
  if (!isRecord(snapshot)) {
    errors.push("Space reservation pricing snapshot must be structured.");
    return;
  }
  if (snapshot.organizationId !== reservation.organizationId) {
    errors.push("Space pricing snapshot organization must match reservation.");
  }
  if (snapshot.spaceId !== reservation.spaceId) {
    errors.push("Space pricing snapshot Space ID must match reservation.");
  }
  if (snapshot.reservationId !== reservation.reservationId) {
    errors.push("Space pricing snapshot reservation ID must match reservation.");
  }
  if (!snapshot.pricingSnapshotId.startsWith("spsnap_")) {
    errors.push("Space pricing snapshot ID must be deterministic.");
  }
  if (snapshot.currency !== "usd") {
    errors.push("Space pricing snapshot currency must be usd.");
  }
  if (!spacePricingTypes.includes(snapshot.pricingType)) {
    errors.push("Space pricing snapshot type must be canonical.");
  }
  if (!spaceRateUnits.includes(snapshot.rateUnit)) {
    errors.push("Space pricing snapshot rate unit must be canonical.");
  }
  if (typeof snapshot.agreementRequired !== "boolean") {
    errors.push("Space pricing snapshot agreement required flag must be boolean.");
  }
  if (
    snapshot.agreementRequired &&
    (snapshot.agreementId === null ||
      !safeAgreementIdPattern.test(snapshot.agreementId) ||
      snapshot.agreementVersion === null ||
      !Number.isSafeInteger(snapshot.agreementVersion) ||
      snapshot.agreementVersion < 1 ||
      typeof snapshot.agreementBodyHash !== "string" ||
      snapshot.agreementBodyHash.length < 12 ||
      snapshot.agreementBodySnapshot === null ||
      snapshot.agreementTitleSnapshot === null)
  ) {
    errors.push("Space pricing snapshot must freeze required agreement details.");
  }
  if (snapshot.agreementBodySnapshot !== null) {
    validateLocalizedText(
      "Space pricing snapshot agreement body",
      snapshot.agreementBodySnapshot,
      6000,
      errors,
    );
  }
  if (snapshot.agreementTitleSnapshot !== null) {
    validateLocalizedText(
      "Space pricing snapshot agreement title",
      snapshot.agreementTitleSnapshot,
      180,
      errors,
    );
  }
  for (const [label, value] of [
    ["rate", snapshot.rateMinor],
    ["minimum charge", snapshot.minimumChargeMinor],
    ["base amount", snapshot.baseAmountMinor],
    ["deposit amount", snapshot.depositAmountMinor],
    ["additional fee amount", snapshot.additionalFeeAmountMinor],
    ["platform fee", snapshot.platformFeeMinor],
    ["organization net", snapshot.organizationNetMinor],
    ["total amount", snapshot.totalAmountMinor],
  ] as const) {
    if (!isMinorAmount(value)) {
      errors.push(`Space pricing snapshot ${label} must use integer minor units.`);
    }
  }
  if (!Number.isSafeInteger(snapshot.durationMinutes) || snapshot.durationMinutes <= 0) {
    errors.push("Space pricing snapshot duration must be positive minutes.");
  }
  if (!Number.isSafeInteger(snapshot.billableMinutes) || snapshot.billableMinutes <= 0) {
    errors.push("Space pricing snapshot billable duration must be positive minutes.");
  }
  if (!Number.isSafeInteger(snapshot.pricingVersion) || snapshot.pricingVersion < 1) {
    errors.push("Space pricing snapshot version must be positive.");
  }
  validateSpaceFixedFees(snapshot.fixedFees, errors);
  const fixedFeeTotal = snapshot.fixedFees.reduce((sum, fee) => sum + fee.amountMinor, 0);
  if (fixedFeeTotal !== snapshot.additionalFeeAmountMinor) {
    errors.push("Space pricing snapshot fixed fees must total the additional fee amount.");
  }
  if (
    snapshot.totalAmountMinor !==
    snapshot.baseAmountMinor + snapshot.depositAmountMinor + snapshot.additionalFeeAmountMinor
  ) {
    errors.push("Space pricing snapshot total must equal base, deposit, and fixed fees.");
  }
  if (snapshot.totalAmountMinor !== snapshot.organizationNetMinor + snapshot.platformFeeMinor) {
    errors.push("Space pricing snapshot total must equal organization net plus platform fee.");
  }
  validateRequiredText("Space pricing snapshot calculator", snapshot.calculatedBy, 160, errors);
  if (snapshot.source !== "experience_forty_one") {
    errors.push("Space pricing snapshot source must be experience_forty_one.");
  }
}

function validateSpaceAgreementAcceptanceSnapshot(
  acceptance: SpaceAgreementAcceptanceSnapshot,
  reservation: SpaceReservationRecord,
  errors: string[],
): void {
  if (!isRecord(acceptance)) {
    errors.push("Space agreement acceptance must be structured.");
    return;
  }
  if (acceptance.organizationId !== reservation.organizationId) {
    errors.push("Space agreement acceptance organization must match reservation.");
  }
  if (acceptance.spaceId !== reservation.spaceId) {
    errors.push("Space agreement acceptance Space ID must match reservation.");
  }
  if (acceptance.reservationId !== reservation.reservationId) {
    errors.push("Space agreement acceptance reservation ID must match reservation.");
  }
  if (acceptance.acceptedByPersonId !== reservation.requesterPersonId) {
    errors.push("Space agreement acceptance must belong to the requester.");
  }
  if (!acceptance.acceptanceId.startsWith("spaccept_")) {
    errors.push("Space agreement acceptance ID must be deterministic.");
  }
  if (!safeAgreementIdPattern.test(acceptance.agreementId)) {
    errors.push("Space agreement acceptance agreement ID must be safe.");
  }
  if (!Number.isSafeInteger(acceptance.agreementVersion) || acceptance.agreementVersion < 1) {
    errors.push("Space agreement acceptance version must be positive.");
  }
  if (
    typeof acceptance.agreementBodyHash !== "string" ||
    acceptance.agreementBodyHash.length < 12
  ) {
    errors.push("Space agreement acceptance must snapshot agreement hash.");
  }
  validateLocalizedText(
    "Space agreement acceptance title",
    acceptance.agreementTitleSnapshot,
    180,
    errors,
  );
  if (!safePersonId(acceptance.authUid)) {
    errors.push("Space agreement acceptance auth UID must be safe.");
  }
  if (acceptance.source !== "experience_forty_one") {
    errors.push("Space agreement acceptance source must be experience_forty_one.");
  }
}

function validateSpaceFixedFees(fees: SpaceFixedFee[], errors: string[]): void {
  if (!Array.isArray(fees) || fees.length > 8) {
    errors.push("Space fixed fees must be a bounded list.");
    return;
  }
  for (const fee of fees) {
    if (!isRecord(fee)) {
      errors.push("Space fixed fee must be structured.");
      continue;
    }
    if (!safeAgreementIdPattern.test(fee.feeId)) {
      errors.push("Space fixed fee ID must be safe.");
    }
    validateLocalizedText("Space fixed fee label", fee.label, 120, errors);
    if (!isMinorAmount(fee.amountMinor)) {
      errors.push("Space fixed fee amount must use integer minor units.");
    }
  }
}

function validateOperatingHours(hours: SpaceOperatingHours, errors: string[]): void {
  if (!isRecord(hours)) {
    errors.push("Space operating hours must be a weekly map.");
    return;
  }

  for (const day of spaceDayIds) {
    const value = hours[day];
    if (typeof value !== "string" || value.length > 180) {
      errors.push(`Space operating hours for ${day} must be a compact string.`);
      continue;
    }
    if (!isValidOperatingHoursValue(value)) {
      errors.push(`Space operating hours for ${day} must be closed or HH:mm-HH:mm intervals.`);
    }
  }
}

function isValidOperatingHoursValue(value: string): boolean {
  if (value === "closed") {
    return true;
  }

  const intervals = value.split(",");
  if (intervals.length < 1 || intervals.length > 3) {
    return false;
  }

  return intervals.every((interval) => {
    const match = interval.match(timeIntervalPattern);
    if (!match) {
      return false;
    }
    const startsAt = match[1];
    const endsAt = match[3];
    return (
      startsAt !== undefined &&
      endsAt !== undefined &&
      minutesFromTime(startsAt) < minutesFromTime(endsAt)
    );
  });
}

function isWithinOperatingHours(value: string | undefined, minuteOfDay: number): boolean {
  if (!value || value === "closed" || !isValidOperatingHoursValue(value)) {
    return false;
  }

  return value.split(",").some((interval) => {
    const match = interval.match(timeIntervalPattern);
    if (!match) {
      return false;
    }
    const startsAt = match[1];
    const endsAt = match[3];
    return (
      startsAt !== undefined &&
      endsAt !== undefined &&
      minuteOfDay >= minutesFromTime(startsAt) &&
      minuteOfDay < minutesFromTime(endsAt)
    );
  });
}

function isIntervalWithinOperatingHours(
  startsAt: Date,
  endsAt: Date,
  space: Pick<SpaceRecord, "operatingHours" | "timezone">,
): boolean {
  const start = localDayAndMinute(startsAt, space.timezone);
  const endMinusOneMs = new Date(endsAt.getTime() - 1);
  const end = localDayAndMinute(endMinusOneMs, space.timezone);

  if (start.day !== end.day) {
    return false;
  }

  const schedule = space.operatingHours[start.day];
  return (
    isWithinOperatingHours(schedule, start.minuteOfDay) &&
    isWithinOperatingHours(schedule, end.minuteOfDay)
  );
}

function localDayAndMinute(
  instant: Date,
  timezone: string,
): {
  day: SpaceDayId;
  minuteOfDay: number;
} {
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    timeZone: timezone,
    weekday: "short",
  }).formatToParts(instant);
  const weekday = parts.find((part) => part.type === "weekday")?.value ?? "Mon";
  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? "0");
  const minute = Number(parts.find((part) => part.type === "minute")?.value ?? "0");

  return {
    day: weekdayToDayId(weekday),
    minuteOfDay: hour * 60 + minute,
  };
}

function weekdayToDayId(value: string): SpaceDayId {
  switch (value) {
    case "Tue":
      return "tuesday";
    case "Wed":
      return "wednesday";
    case "Thu":
      return "thursday";
    case "Fri":
      return "friday";
    case "Sat":
      return "saturday";
    case "Sun":
      return "sunday";
    case "Mon":
    default:
      return "monday";
  }
}

function minutesFromTime(value: string): number {
  const [hour = 0, minute = 0] = value.split(":").map(Number);
  return hour * 60 + minute;
}

function validateAccessibility(accessibility: SpaceAccessibility, errors: string[]): void {
  if (!isRecord(accessibility)) {
    errors.push("Space accessibility must be structured.");
    return;
  }

  if (
    !Array.isArray(accessibility.features) ||
    accessibility.features.length > spaceAccessibilityFeatureIds.length
  ) {
    errors.push("Space accessibility features must be a bounded list.");
  } else if (
    accessibility.features.some((feature) => !spaceAccessibilityFeatureIds.includes(feature))
  ) {
    errors.push("Space accessibility features must use canonical identifiers.");
  }
  if (accessibility.notes !== null) {
    validateLocalizedText("Space accessibility notes", accessibility.notes, 240, errors);
  }
}

function validateMediaReference(
  label: string,
  media: SpaceMediaReference | null,
  space: Pick<SpaceRecord, "organizationId" | "spaceId">,
  errors: string[],
): void {
  if (media === null) {
    return;
  }
  if (!isRecord(media)) {
    errors.push(`${label} must be structured.`);
    return;
  }

  const expectedPrefix = `organizations/${space.organizationId}/spaces/${space.spaceId}/`;
  if (
    !media.storagePath.startsWith(expectedPrefix) ||
    !safeStoragePathPattern.test(media.storagePath)
  ) {
    errors.push(`${label} storage path must stay under the owning Space.`);
  }
  validateLocalizedText(`${label} alt text`, media.altText, 160, errors);
  if (
    media.visibility !== "inherit" &&
    media.visibility !== "connected" &&
    media.visibility !== "public"
  ) {
    errors.push(`${label} visibility must be canonical.`);
  }
  if (!Number.isSafeInteger(media.sortOrder) || media.sortOrder < 0 || media.sortOrder > 99) {
    errors.push(`${label} sort order must be a small integer.`);
  }
}

function validateLocalizedText(
  label: string,
  value: SpaceLocalizedText,
  maxLength: number,
  errors: string[],
): void {
  if (!isRecord(value)) {
    errors.push(`${label} must be localized text.`);
    return;
  }
  validateRequiredText(`${label} English`, value.en, maxLength, errors);
  validateOptionalText(`${label} Arabic`, value.ar, maxLength, errors);
}

function validateRequiredText(
  label: string,
  value: unknown,
  maxLength: number,
  errors: string[],
): void {
  if (typeof value !== "string" || value.trim().length === 0) {
    errors.push(`${label} is required.`);
    return;
  }
  if (value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer.`);
  }
}

function validateOptionalText(
  label: string,
  value: unknown,
  maxLength: number,
  errors: string[],
): void {
  if (value === null || value === undefined) {
    return;
  }
  if (typeof value !== "string" || value.length > maxLength) {
    errors.push(`${label} must be ${maxLength} characters or fewer when present.`);
  }
}

function validateRequesterSnapshot(
  requester: SpaceReservationRequesterSnapshot,
  errors: string[],
): void {
  if (!isRecord(requester)) {
    errors.push("Space reservation requester snapshot must be structured.");
    return;
  }
  validateRequiredText(
    "Space reservation requester display name",
    requester.displayName,
    160,
    errors,
  );
  validateOptionalText("Space reservation requester email", requester.email, 320, errors);
}

function safePersonId(value: unknown): boolean {
  return typeof value === "string" && /^[A-Za-z0-9._:-]{1,128}$/.test(value);
}

function isMinorAmount(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= maximumSpacePaymentMinor
  );
}

function stableTextHash(value: string): string {
  let first = 0x811c9dc5;
  let second = 0x01000193;
  for (let index = 0; index < value.length; index += 1) {
    const code = value.charCodeAt(index);
    first ^= code;
    first = Math.imul(first, 0x01000193) >>> 0;
    second ^= code + index;
    second = Math.imul(second, 0x811c9dc5) >>> 0;
  }
  return `${first.toString(16).padStart(8, "0")}${second.toString(16).padStart(8, "0")}`.repeat(4);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
